
import { mockApiCall } from '@/utils/mockApiCall';
import { AdminUser } from '@/types/adminUser';

/**
 * Invites a super admin manager to the platform
 * @param data Manager data including name, email, and phone
 * @returns Promise<boolean> indicating success or failure
 */
export const inviteSuperAdminManager = async (data: { 
  name: string; 
  email: string; 
  phone: string; 
}): Promise<boolean> => {
  console.log('Inviting super admin manager:', data);
  
  try {
    // In a real app, this would be an API call
    await mockApiCall(1000); // Simulate API latency
    
    // For demo purposes, we'll return success for valid email formats
    if (!data.email || !data.email.includes('@')) {
      console.error('Invalid email format');
      return false;
    }
    
    if (!data.name || data.name.length < 2) {
      console.error('Name is too short');
      return false;
    }
    
    // In a real app, more validation would happen server-side
    
    console.log('Super admin manager invitation successful');
    return true;
  } catch (error) {
    console.error('Error inviting super admin manager:', error);
    return false;
  }
};

/**
 * Retrieves a list of admin users
 * @returns Promise<AdminUser[]> list of admin users
 */
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  try {
    // In a real app, this would be an API call
    await mockApiCall(800);
    
    // Mock data for admin users
    const mockUsers: AdminUser[] = [
      {
        id: 'admin-1',
        name: 'John Doe',
        email: 'john@example.com',
        role: 'admin',
        status: 'active',
        phone: '+1234567890',
        lastLogin: new Date(2025, 3, 10)
      },
      {
        id: 'admin-2',
        name: 'Jane Smith',
        email: 'jane@example.com',
        role: 'manager',
        status: 'active',
        phone: '+1987654321',
        lastLogin: new Date(2025, 3, 15)
      },
      {
        id: 'admin-3',
        name: 'Robert Johnson',
        email: 'robert@example.com',
        role: 'admin',
        status: 'pending',
        phone: '+1456789012'
      }
    ];
    
    return mockUsers;
  } catch (error) {
    console.error('Error getting admin users:', error);
    return [];
  }
};

/**
 * Adds a new admin user
 * @param userData User data to add
 * @returns Promise<AdminUser> The added user
 */
export const addAdminUser = async (userData: Omit<AdminUser, 'id' | 'status'>): Promise<AdminUser> => {
  try {
    // In a real app, this would be an API call
    await mockApiCall(1000);
    
    // Validate email
    if (!userData.email || !userData.email.includes('@')) {
      throw new Error('Invalid email format');
    }
    
    // Create a new user with the provided data
    const newUser: AdminUser = {
      id: `admin-${Date.now()}`,
      ...userData,
      status: 'active'
    };
    
    return newUser;
  } catch (error) {
    console.error('Error adding admin user:', error);
    throw error;
  }
};

/**
 * Deletes an admin user
 * @param userId ID of the user to delete
 * @returns Promise<void>
 */
export const deleteAdminUser = async (userId: string): Promise<void> => {
  try {
    // In a real app, this would be an API call
    await mockApiCall(800);
    
    // In a real app, we would delete the user from the database
    console.log(`User ${userId} deleted successfully`);
  } catch (error) {
    console.error('Error deleting admin user:', error);
    throw error;
  }
};

/**
 * Invites an admin user to the platform
 * @param email Email of the user to invite
 * @param name Name of the user
 * @param role Role to assign to the user
 * @returns Promise<void>
 */
export const inviteAdminUser = async (email: string, name: string, role: 'admin' | 'manager'): Promise<void> => {
  try {
    // In a real app, this would be an API call
    await mockApiCall(1200);
    
    // Validate email
    if (!email || !email.includes('@')) {
      throw new Error('Invalid email format');
    }
    
    // In a real app, this would create an invitation in the database
    // and send an email to the user
    console.log(`Invitation sent to ${email} for role ${role}`);
  } catch (error) {
    console.error('Error inviting admin user:', error);
    throw error;
  }
};
